import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import logoFull from '@/assets/fluxo-logo-full.png';
import { Button } from '@/components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Package,
  Truck,
  Users,
  ShoppingCart,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Snowflake,
  Building2,
  User,
  KeyRound,
  ShieldCheck,
  AlertCircle,
  Sparkles,
  Database,
  Ticket,
  BarChart3,
  Calendar,
  // New icons for verticals:
  DollarSign, GraduationCap, Receipt, MessageSquare, ClipboardCheck,
  FileText, Wrench, Cog, UserPlus, Home, TrendingUp, Warehouse,
  FileCheck, HeartPulse, FolderOpen, Pill, BedDouble, UtensilsCrossed,
  Factory, Monitor, HardDrive, Settings, Briefcase, ClipboardList
} from 'lucide-react';

const TenantDashboardLayout = () => {
  const { tenant, tenantUser, tenantSlug, logoutTenant, changePassword, loading } = useTenant();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Change Password state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const handleLogout = () => {
    logoutTenant();
    navigate(`/tenant/${tenantSlug}/login`);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error("Las nuevas contraseñas no coinciden");
      return;
    }

    if (passwordForm.new.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }

    setIsChangingPassword(true);
    const result = await changePassword(passwordForm.current, passwordForm.new);
    setIsChangingPassword(false);

    if (result.success) {
      toast.success("Contraseña actualizada correctamente");
      setPasswordFormOpen(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } else {
      toast.error(result.error);
    }
  };

  // Dynamic nav items based on tenant vertical config
  const getNavItems = () => {
    // Si el tenant tiene sidebar_order (Fase 10), usamos el menú dinámico
    if (tenant?.sidebar_order && tenant.sidebar_order.length > 0) {
      const config = tenant.collections_config || {};
      
      // Diccionario de íconos
      const iconMap = {
        'dashboard': LayoutDashboard,
        'reportes': BarChart3,
        'engine': Database,
        'config': Sparkles,
        'notificaciones': Bell,
        // Silos comunes:
        'catalogos': ShoppingCart,
        'ventas': DollarSign,
        'tickets': Ticket,
        'citas': Calendar,
        'clientes': Users,
        // Silos especializados:
        'admisiones': GraduationCap,
        'cobranza_escolar': Receipt,
        'comunicacion': MessageSquare,
        'asistencia': ClipboardCheck,
        'cotizaciones': FileText,
        'servicios': Wrench,
        'refacciones': Cog,
        'leads': UserPlus,
        'propiedades': Home,
        'pipeline': TrendingUp,
        'pedidos': Package,
        'rutas': Truck,
        'inventario': Warehouse,
        'facturacion': FileCheck,
        'pacientes': HeartPulse,
        'expedientes': FolderOpen,
        'recetas': Pill,
        'reservas': BedDouble,
        'menu': UtensilsCrossed,
        'proveedores': Factory,
        'proyectos_ti': Monitor,
        'activos_ti': HardDrive,
        'vacantes': Briefcase,
        'postulaciones': ClipboardList
      };

      // Mapeo de nombres estáticos (fallback si no están en collections_config)
      const labelMap = {
        'dashboard': 'Dashboard',
        'reportes': 'Reportes',
        'engine': 'Data Engine',
        'config': 'Autogestión Pro',
        'notificaciones': 'Notificaciones'
      };

      return tenant.sidebar_order.map(key => {
        // Encontrar el nombre a mostrar (priorizar configuración de base de datos)
        const displayLabel = config[key]?.display_name || labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ');
        
        return {
          path: key,
          icon: iconMap[key] || Package, // Fallback icon
          label: displayLabel
        };
      });
    }

    return [];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-slate-950">
        <div className="w-8 h-8 border-4 border-slate-700 border-t-cyan-400 rounded-full animate-spin"></div>
        <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px] animate-pulse">Armando Portal...</p>
      </div>
    );
  }

  const navItems = getNavItems();
  const isUnprovisioned = !tenant?.sidebar_order || tenant.sidebar_order.length === 0;
  
  // Chameleon System: Dynamic branding
  const primaryColor = tenant?.primary_color || '#06b6d4'; // Default to Cyan
  const dynamicStyles = {
    '--tenant-primary': primaryColor,
    '--tenant-primary-transparent': `${primaryColor}1A`, // 10% opacity
    '--tenant-gradient': `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`
  };

  return (
    <div className="min-h-screen bg-background flex" style={dynamicStyles}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 h-screen z-50
        w-64 bg-card border-r transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full overflow-hidden">
          {/* Tenant Logo */}
          <div className="p-4 border-b flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:scale-110 relative"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: `0 8px 32px 0 rgba(0, 0, 0, 0.37), 0 0 15px ${primaryColor}1a`
                }}
              >
                {tenant?.logo_url ? (
                  <img src={tenant.logo_url} alt={tenant.name} className="w-7 h-7 object-contain relative z-10 drop-shadow-md" />
                ) : (
                  <Building2 className="w-6 h-6 text-white relative z-10" />
                )}
              </div>
              <div>
                <h1 className="text-lg font-bold font-outfit truncate max-w-[140px]">{tenant?.name}</h1>
                <p className="text-xs text-muted-foreground">Portal</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation - Scrollable Area */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar py-6">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={`/tenant/${tenantSlug}/${item.path}`}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                  ${isActive 
                    ? 'text-[var(--tenant-primary)]' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
                style={({ isActive }) => isActive ? { backgroundColor: 'var(--tenant-primary-transparent)' } : {}}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Footer Section - Fixed at bottom (Master Console Style) */}
          <div className="shrink-0 space-y-3 p-4 border-t bg-card/50 backdrop-blur-md">
            {/* Powered by Fluxo Co (Preserved as requested) */}
            <div className="p-3 rounded-xl border flex flex-col items-start gap-2 group transition-all duration-300"
                 style={{ 
                   backgroundColor: 'var(--tenant-primary-transparent)', 
                   borderColor: 'rgba(255,255,255,0.05)'
                 }}>
               <div className="flex items-center gap-2">
                  <span className="text-[9px] font-black uppercase tracking-[0.2em]" 
                        style={{ color: 'var(--tenant-primary)' }}>Sistema Operativo</span>
               </div>
               <div className="flex items-center gap-2">
                  <img 
                    src={logoFull} 
                    alt="Fluxo Co" 
                    className="h-4 object-contain transition-all duration-500 group-hover:scale-105"
                    style={{ filter: "invert(1) hue-rotate(185deg) brightness(1.2)" }}
                  />
               </div>
               <p className="text-[10px] leading-tight font-medium opacity-50">
                 Habilitado por infraestructura Fluxo Co.
               </p>
            </div>

            {/* User section - Master Console Style */}
            <div className="group relative p-2.5 rounded-2xl bg-white/5 border border-white/5 hover:bg-[var(--tenant-primary-transparent)] hover:border-[var(--tenant-primary)]/20 transition-all duration-500">
              <div className="flex items-center gap-2.5">
                <Avatar className="h-8 w-8 border-2 border-[var(--tenant-primary)]/20 group-hover:border-[var(--tenant-primary)]/40 transition-colors">
                  <AvatarFallback className="bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] text-[10px] font-bold">
                    {tenantUser?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate leading-tight">{tenantUser?.name}</p>
                  <p className="text-[8px] text-[var(--tenant-primary)]/60 font-black uppercase tracking-[0.1em] mt-0.5">
                    {tenantUser?.role || 'Portal Admin'}
                  </p>
                </div>
              </div>
              
              {/* Compact Action Row */}
              <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/5">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 rounded-lg text-slate-400 hover:text-[var(--tenant-primary)] hover:bg-[var(--tenant-primary-transparent)] transition-all"
                  onClick={() => setPasswordFormOpen(true)}
                  title="Configuración"
                >
                  <Settings className="w-3.5 h-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  onClick={handleLogout}
                  title="Cerrar Sesión"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 lg:px-6">
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Plataforma de Flujos Operativos
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {isUnprovisioned ? (
            <div className="flex flex-col items-center justify-center h-[80%] max-w-lg mx-auto text-center space-y-6">
              <div className="w-24 h-24 rounded-[2rem] bg-slate-900 border border-white/5 flex items-center justify-center mb-2 shadow-xl shadow-black/50">
                <Database className="w-10 h-10 text-slate-500" />
              </div>
              <div>
                <h2 className="text-3xl font-bold font-outfit text-white tracking-tight">Arquitectura Pendiente</h2>
                <p className="text-slate-400 text-lg mt-3 leading-relaxed">
                  No tienes datos que administrar en este momento. La plataforma se encuentra temporalmente suspendida o a la espera de inicialización.
                </p>
              </div>
              <div className="p-5 bg-blue-500/10 border border-blue-500/20 rounded-2xl w-full text-left mt-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-blue-100 font-medium font-outfit text-lg">Contacta a tu Administrador</h3>
                    <p className="text-blue-200/70 text-sm mt-1.5 leading-relaxed">
                      Para continuar, comunícate con un Consultor de <b>Fluxo Co.</b> para que habiliten la estructura de contenedores de datos configurada para tu giro de negocio.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={passwordFormOpen} onOpenChange={setPasswordFormOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-outfit">
              <ShieldCheck className="w-5 h-5 text-primary" />
              Actualizar Contraseña
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current">Contraseña Actual</Label>
              <Input
                id="current"
                type="password"
                required
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                placeholder="••••••••"
                className="font-mono"
              />
            </div>
            
            <div className="pt-2 border-t space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new">Nueva Contraseña</Label>
                <Input
                  id="new"
                  type="password"
                  required
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirmar Nueva Contraseña</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  placeholder="••••••••"
                  className="font-mono"
                />
              </div>
            </div>
            
            {passwordForm.new && passwordForm.confirm && passwordForm.new !== passwordForm.confirm && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded">
                <AlertCircle className="w-4 h-4" />
                Las contraseñas no coinciden
              </div>
            )}

            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setPasswordFormOpen(false)}
                disabled={isChangingPassword}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? "Actualizando..." : "Guardar Cambios"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDashboardLayout;
